import { useState } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import { useServicePackagesQuery } from '@/graphql/queries/servicePackages.generated';
import { useSaveServicePackageRevisionMutation } from '@/graphql/mutations/saveServicePackageRevision.generated';
import { useCopyServicePackageToCurrencyMutation } from '@/graphql/mutations/copyServicePackageToCurrency.generated';
import { useCopyServicePackageToTermMutation } from '@/graphql/mutations/copyServicePackageToTerm.generated';
import type { RevisionFormState } from './types';
import { PACKAGE_TYPE_LABELS } from './types';
import { ServicePackageContext } from './ServicePackageContext';
import { Routes } from '@/utils/routes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ServicePackageListPage from './ServicePackageListPage';
import ServicePackageDetailPage from './ServicePackageDetailPage';
import i18n from '@/utils/i18n';

// ── Router ─────────────────────────────────────────────────────────────────────

export default function ServicePackagesRouter() {
  const history = useHistory();

  const { data, loading } = useServicePackagesQuery();
  const packages = data?.servicePackages ?? [];

  const [saveRevision] = useSaveServicePackageRevisionMutation();
  const [copyToCurrency] = useCopyServicePackageToCurrencyMutation();
  const [copyToTerm] = useCopyServicePackageToTermMutation();

  // ── Copy-to-currency dialog state ────────────────────────────────────────
  const [cloningPkgId, setCloningPkgId] = useState<string | null>(null);
  const [cloneTargetCurrency, setCloneTargetCurrency] = useState('');
  const [copying, setCopying] = useState(false);

  const cloningPkg = cloningPkgId
    ? packages.find((p) => p.id === cloningPkgId)
    : null;

  const takenCurrenciesForClone = cloningPkg
    ? packages
        .filter((p) => p.package_type === cloningPkg.package_type)
        .map((p) => p.currency.toUpperCase())
    : [];

  const cloneTargetNorm = cloneTargetCurrency.trim().toUpperCase();
  const cloneValid =
    /^[A-Z]{2,4}$/.test(cloneTargetNorm) &&
    !takenCurrenciesForClone.includes(cloneTargetNorm);

  const closeClone = () => {
    setCloningPkgId(null);
    setCloneTargetCurrency('');
  };

  // ── Shared handlers (exposed via context) ────────────────────────────────

  const handleSave = async (
    pkgId: string,
    term: number,
    data: RevisionFormState,
  ) => {
    const pkg = packages.find((p) => p.id === pkgId);
    if (!pkg) return;
    await saveRevision({
      variables: {
        input: {
          currency: pkg.currency,
          package_type: pkg.package_type,
          min_contract_term_months: term,
          monthly_service_fee_in_cents: parseInt(
            data.monthly_service_fee_in_cents,
            10,
          ),
          onboarding_fee_in_cents: parseInt(data.onboarding_fee_in_cents, 10),
          ops_order_price_in_cents: parseInt(data.ops_order_price_in_cents, 10),
          free_checkouts: data.free_checkouts.map((fc) => ({
            segment: fc.segment,
            amount: parseInt(fc.amount, 10),
          })),
          note: data.note || null,
          allowances: data.asset_allowances.map((a) => ({
            item_type_id: String(a.item_type_id),
            monthly_quantity: parseInt(a.included_amount, 10),
          })),
        },
      },
      refetchQueries: ['servicePackages'],
    });
  };

  const handleCopyToTerm = async (
    sourceRevisionId: string,
    targetTerm: number,
  ) => {
    await copyToTerm({
      variables: {
        input: {
          source_revision_id: sourceRevisionId,
          target_min_contract_term_months: targetTerm,
        },
      },
      refetchQueries: ['servicePackages'],
    });
  };

  const requestClone = (pkgId: string) => {
    setCloneTargetCurrency('');
    setCloningPkgId(pkgId);
  };

  const handleClone = async () => {
    if (!cloningPkg || !cloneValid) return;
    setCopying(true);
    try {
      const result = await copyToCurrency({
        variables: {
          input: {
            source_package_id: cloningPkg.id,
            target_currency: cloneTargetNorm,
          },
        },
        refetchQueries: ['servicePackages'],
      });
      closeClone();
      const newPkg = result.data?.copyServicePackageToCurrency.package;
      if (newPkg) {
        history.push(
          Routes.servicePackage(newPkg.currency, newPkg.package_type),
        );
      }
    } finally {
      setCopying(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <ServicePackageContext.Provider
      value={{ packages, loading, handleSave, handleCopyToTerm, requestClone }}
    >
      <Switch>
        <Route
          exact
          path={Routes.servicePackages()}
          component={ServicePackageListPage}
        />
        <Route
          path={Routes.servicePackage()}
          component={ServicePackageDetailPage}
        />
      </Switch>

      {/* Copy-to-currency dialog — always mounted, works from both list and detail views */}
      <Dialog
        open={cloningPkgId !== null}
        onOpenChange={(o) => !o && !copying && closeClone()}
      >
        <DialogContent className="tw:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {i18n.t('ServicePackage:copyPackageToCurrency', {
                packageType: cloningPkg
                  ? PACKAGE_TYPE_LABELS[cloningPkg.package_type]
                  : '',
              })}
            </DialogTitle>
          </DialogHeader>

          <div className="tw:flex tw:flex-col tw:gap-4 tw:py-2">
            <p className="tw:text-sm tw:text-muted-foreground">
              {i18n.t('ServicePackage:copyDescription')}
            </p>
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label htmlFor="clone-currency">
                {i18n.t('ServicePackage:targetCurrency')}
              </Label>
              <Input
                id="clone-currency"
                placeholder="e.g. USD"
                value={cloneTargetCurrency}
                onChange={(e) =>
                  setCloneTargetCurrency(e.target.value.toUpperCase())
                }
                maxLength={4}
                disabled={copying}
              />
              {cloneTargetNorm.length > 0 &&
                takenCurrenciesForClone.includes(cloneTargetNorm) && (
                  <span className="tw:text-xs tw:text-destructive">
                    {i18n.t('ServicePackage:planAlreadyExists', {
                      packageType: cloningPkg
                        ? PACKAGE_TYPE_LABELS[cloningPkg.package_type]
                        : '',
                      currency: cloneTargetNorm,
                    })}
                  </span>
                )}
              {takenCurrenciesForClone.length > 0 && (
                <span className="tw:text-xs tw:text-muted-foreground">
                  {i18n.t('ServicePackage:alreadyConfigured', {
                    currencies: takenCurrenciesForClone.join(', '),
                  })}
                </span>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeClone} disabled={copying}>
              {i18n.t('Common:cancel')}
            </Button>
            <Button onClick={handleClone} disabled={!cloneValid || copying}>
              {copying
                ? i18n.t('ServicePackage:copying')
                : i18n.t('ServicePackage:copy')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ServicePackageContext.Provider>
  );
}
